import { Box, TextField, Grid, Typography, Button } from '@mui/material'
import { Applicant } from 'store/dashboard/types'
import CustomChart from '../customChart/CustomChart'
import { useEffect, useState } from 'react'
import { formatNumber } from 'store/utils'
import CompetitiveStrengthGap from './competitiveStrengthGap/CompetitiveStrengthGap'
import SectionContainer from '../../../../components/SectionContainer'
import { GAP_IMPLICATIONS } from './gapImpicationsTexts'
import { getGapCategory } from './gapThresholds'
import UnderstandingSection from 'containers/dashboard/multisectorReport/sections/chartsSection/purePlaySection/understandingSection/UnderstandingSection'
import {
  calculateGap,
  getNewPercentage,
} from 'containers/dashboard/multisectorReport/sections/chartsSection/purePlaySection/purePlayCalculations'

const ArrowIcon = ({ width = 300 }) => {
  const arrowHeadSize = 10
  const height = 20
  const color = 'black'
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
      <line x1={arrowHeadSize} y1={height / 2} x2={width - arrowHeadSize} y2={height / 2} stroke={color} strokeWidth="2" />
      <polygon points={`${0},${height / 2} ${arrowHeadSize},${height / 2 - 5} ${arrowHeadSize},${height / 2 + 5}`} fill={color} />
      <polygon
        points={`${width},${height / 2} ${width - arrowHeadSize},${height / 2 - 5} ${width - arrowHeadSize},${height / 2 + 5}`}
        fill={color}
      />
    </svg>
  )
}

type Props = {
  competitors: Applicant[]
  applicantsFirstPerSubgroup: Applicant[]
  topCompaniesNumber: number
  setTopCompaniesNumber: (num: number) => void
  selectedSubgroups: string[]
}

export default function PurePlaySection({
  competitors,
  applicantsFirstPerSubgroup,
  topCompaniesNumber,
  setTopCompaniesNumber,
  selectedSubgroups,
}: Props) {
  const [chartData, setChartData] = useState<Applicant[]>([])
  const [gap, setGap] = useState<number>(0)
  const [gapImplications, setGapImplications] = useState<{ implications: string[] }>({ implications: [] })
  const [averageCompetitors, setAverageCompetitors] = useState<number>(0)
  const targetCompany = applicantsFirstPerSubgroup[0]
  const targetCompanyName = targetCompany.applicant_original_name

  useEffect(() => {
    console.log('PurePlaySection useEffect triggered')

    // Prepare chart data for the chart
    // Calculate percentages based on selected subgroups
    // For each company: sum whales_patents_in_category across selected subgroups / whales_patent_count
    let updatedTargetCompany: Applicant = {
      ...targetCompany,
      whales_patent_in_percent: getNewPercentage(targetCompany, selectedSubgroups),
    }

    const newChartData: Applicant[] = [updatedTargetCompany, ...competitors].filter(Boolean).map(company => ({
      ...company,
      whales_patent_in_percent: Math.round(company.whales_patent_in_percent ?? 0),
    }))

    setChartData(newChartData)

    // ---------- NO COMPETITORS CASE ----------
    if (competitors.length === 0) {
      return
    }

    // ---------- NORMAL FLOW ----------
    const gap = calculateGap(updatedTargetCompany, competitors) // This function calculates the gap between the target company and the average of competitors
    setGap(gap)

    const category = getGapCategory(gap)
    const gapImplications = GAP_IMPLICATIONS[category]
    setGapImplications(gapImplications)

    const averageCompetitors: number = formatNumber(
      competitors.reduce((acc, curr) => acc + curr.whales_patent_in_percent, 0) / competitors.length,
    )
    setAverageCompetitors(averageCompetitors)
  }, [topCompaniesNumber, applicantsFirstPerSubgroup, selectedSubgroups])

  const InfoText = () => {
    return (
      <Box
        pl={5}
        pt={2}
        pr={3}
        color="normalGrey.main"
        sx={{
          '& .MuiTypography-root': {
            fontSize: 15,
          },
        }}>
        <Typography sx={{ whiteSpace: 'pre-line' }}>
          The top {topCompaniesNumber} competitors average a Pure Play score of {averageCompetitors}%. This creates a Strategic
          Focus gap analysis of {formatNumber(gap)}%.
        </Typography>
        <Typography my={3}>{targetCompany.applicant_original_name} implications:</Typography>
        {gapImplications.implications.map((implicationText, index) => (
          <Typography key={index} fontSize={15} color="primary.main" mb={1}>
            {implicationText}
          </Typography>
        ))}
      </Box>
    )
  }

  return (
    <>
      <CompetitiveStrengthGap
        gap={gap}
        topCompaniesNumber={topCompaniesNumber}
        onChangeTopCompaniesNumber={num => setTopCompaniesNumber(num)}
      />
      <UnderstandingSection targetCompanyName={targetCompanyName} />

      <SectionContainer sx={{ p: 3 }}>
        <Typography fontSize={20} pb={2} mt={2}>
          Peer Group Comparison
        </Typography>
        {competitors.length === 0 ? (
          <Typography color="text.secondary" fontSize={15} mt={2}>
            No competitors found based on the selected sectors.
          </Typography>
        ) : (
          <Grid container spacing={2} columns={{ xs: 4, sm: 6, md: 6, lg: 12 }}>
            <Grid size={{ xs: 4, md: 7 }}>
              <Box ml={27} mt={3} mr={2} display={'flex'} alignItems="center" fontSize={12} fontWeight={600}>
                Diversified
                <Box sx={{ mx: 1.5, mt: 0.6 }}>
                  <ArrowIcon />
                </Box>
                Specialised
              </Box>

              {chartData.length && (
                <CustomChart
                  dataset={chartData}
                  xKey={'whales_patent_in_percent'}
                  xLabel={'Pure play %'}
                  topCompaniesNumber={topCompaniesNumber}
                />
              )}
            </Grid>{' '}
            <Grid size={{ xs: 4, md: 5 }}>
              <InfoText />
            </Grid>
          </Grid>
        )}
      </SectionContainer>
    </>
  )
}
